import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Globe, Plus, Edit, Trash2, Search, ArrowLeft 
} from 'lucide-react';
import { countriesApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const AdminCountries = ({ isNew = false }) => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(isNew);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    flag_emoji: '',
    image_url: '',
    description: '',
    requirements: [],
    is_popular: false,
    currency: '',
    language: '',
    capital: '',
    timezone_info: '',
    best_time_to_visit: '',
    important_notes: [],
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_title: '',
    og_description: '',
    og_image: ''
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await countriesApi.getAll();
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCountry) {
        await countriesApi.update(editingCountry.country_id, formData);
        toast.success('Country updated successfully');
      } else {
        await countriesApi.create(formData);
        toast.success('Country created successfully');
      }
      resetForm();
      fetchCountries();
    } catch (error) {
      console.error('Error saving country:', error);
      toast.error('Failed to save country');
    }
  };

  const handleDelete = async (countryId) => {
    if (!window.confirm('Are you sure you want to delete this country? This will also delete all associated visa types.')) {
      return;
    }
    try {
      await countriesApi.delete(countryId);
      toast.success('Country deleted successfully');
      fetchCountries();
    } catch (error) {
      console.error('Error deleting country:', error);
      toast.error('Failed to delete country');
    }
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      name: country.name,
      code: country.code,
      flag_emoji: country.flag_emoji,
      image_url: country.image_url,
      description: country.description,
      requirements: country.requirements || [],
      is_popular: country.is_popular,
      currency: country.currency || '',
      language: country.language || '',
      capital: country.capital || '',
      timezone_info: country.timezone_info || '',
      best_time_to_visit: country.best_time_to_visit || '',
      important_notes: country.important_notes || [],
      seo_title: country.seo_title || '',
      seo_description: country.seo_description || '',
      seo_keywords: country.seo_keywords || '',
      og_title: country.og_title || '',
      og_description: country.og_description || '',
      og_image: country.og_image || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCountry(null);
    setFormData({
      name: '',
      code: '',
      flag_emoji: '',
      image_url: '',
      description: '',
      requirements: [],
      is_popular: false,
      currency: '',
      language: '',
      capital: '',
      timezone_info: '',
      best_time_to_visit: '',
      important_notes: [],
      seo_title: '',
      seo_description: '',
      seo_keywords: '',
      og_title: '',
      og_description: '',
      og_image: ''
    });
    if (isNew) navigate('/admin/countries');
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (showForm) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <DashboardSidebar />
        <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8">
          <Button variant="ghost" onClick={resetForm} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Countries
          </Button>

          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF] max-w-5xl">
            <h1 className="text-2xl font-bold text-[#0A1E40] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingCountry ? 'Edit Country' : 'Add New Country'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1 sm:mt-0">
                  <Label>Country Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., United States"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 sm:mt-0">
                  <Label>Country Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., US"
                    maxLength={2}
                    required
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Flag Emoji</Label>
                  <Input
                    value={formData.flag_emoji}
                    onChange={(e) => setFormData({ ...formData, flag_emoji: e.target.value })}
                    placeholder="e.g., 🇺🇸"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6 ">
                  <Switch
                    checked={formData.is_popular}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                  />
                  <Label>Popular Destination</Label>
                </div>
              </div>

              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  required
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the country..."
                  required
                  className="mt-1.5"
                  rows={3}
                />
              </div>

              {/* Country Info Fields */}
              <div className="border-t border-[#D1CBBF] pt-4 mt-2">
                <p className="text-sm font-semibold text-[#0A1E40] mb-3">Country Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1 sm:mt-0">
                    <Label>Currency</Label>
                    <Input
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      placeholder="e.g., USD (US Dollar)"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 sm:mt-0">
                    <Label>Language</Label>
                    <Input
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      placeholder="e.g., English"
                      className="mt-1.5"/>
                  </div>
                  <div className="col-span-2 sm:col-span-1 sm:mt-0">
                    <Label>Capital</Label>
                    <Input
                      value={formData.capital}
                      onChange={(e) => setFormData({ ...formData, capital: e.target.value })}
                      placeholder="e.g., Washington, D.C."
                      className="mt-1.5"/>
                  </div>
                  <div className="col-span-2 sm:col-span-1 sm:mt-0">
                    <Label>Timezone</Label>
                    <Input
                      value={formData.timezone_info}
                      onChange={(e) => setFormData({ ...formData, timezone_info: e.target.value })}
                      placeholder="e.g., EST/PST (UTC-5 to UTC-8)"
                      className="mt-1.5"/>
                  </div>
                </div>
                <div className="mt-4">
                  <Label>Best Time to Visit</Label>
                  <Input
                    value={formData.best_time_to_visit}
                    onChange={(e) => setFormData({ ...formData, best_time_to_visit: e.target.value })}
                    placeholder="e.g., March to May, September to November"
                    className="mt-1.5"/>
                </div>
              </div>

              <div>
                <Label>Requirements (one per line)</Label>
                <Textarea
                  value={formData.requirements.join('\n')}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value.split('\n').filter(r => r.trim()) })}
                  placeholder="Valid passport&#10;Financial documents&#10;..."
                  className="mt-1.5"
                  rows={4}
                />
              </div>

              <div>
                <Label>Important Notes (one per line)</Label>
                <Textarea
                  value={formData.important_notes.join('\n')}
                  onChange={(e) => setFormData({ ...formData, important_notes: e.target.value.split('\n').filter(n => n.trim()) })}
                  placeholder="Passport must be valid for 6 months&#10;Processing times may vary&#10;..."
                  className="mt-1.5"
                  rows={4}
                />
              </div>

              {/* SEO & OG Tags */}
              <div className="border-t border-[#D1CBBF] pt-4 mt-2">
                <p className="text-sm font-semibold text-[#0A1E40] mb-3">SEO & Open Graph Tags</p>
                <div className="space-y-4">
                  <div>
                    <Label>SEO Title</Label>
                    <Input
                      value={formData.seo_title}
                      onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                      placeholder="e.g., USA Visa - Apply Online for Tourist & Business Visa"
                      className="mt-1.5"
                    />
                    <p className="text-[10px] text-[#9CA3AF] mt-1">Leave empty to auto-generate from country name</p>
                  </div>
                  <div>
                    <Label>SEO Description</Label>
                    <Textarea
                      value={formData.seo_description}
                      onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                      placeholder="Brief SEO-friendly description for search engines (150-160 chars recommended)"
                      className="mt-1.5"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>SEO Keywords</Label>
                    <Input
                      value={formData.seo_keywords}
                      onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                      placeholder="usa visa, us tourist visa, us business visa, us visa fee"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 sm:col-span-1 sm:mt-0">
                      <Label>OG Title</Label>
                      <Input
                        value={formData.og_title}
                        onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                        placeholder="Custom title for social sharing"
                        className="mt-1.5"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1 sm:mt-0">
                      <Label>OG Image URL</Label>
                      <Input
                        value={formData.og_image}
                        onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                        placeholder="https://... (social share image)"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>OG Description</Label>
                    <Textarea
                      value={formData.og_description}
                      onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                      placeholder="Description for social media sharing"
                      className="mt-1.5"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-[#FF5A36] hover:bg-[#E0492B]">
                  {editingCountry ? 'Update Country' : 'Create Country'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="admin-countries-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Manage Countries
            </h1>
            <p className="text-[#4A5568] mt-1">{countries.length} countries configured</p>
          </div>
          <Button 
            onClick={() => setShowForm(true)} 
            className="bg-[#FF5A36] hover:bg-[#E0492B]"
            data-testid="add-country-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Country
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
            <Input
              type="text"
              placeholder="Search countries by name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
            />
          </div>
        </div>

        {/* Countries Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl h-48 animate-pulse" />
            ))}
          </div>
        ) : filteredCountries.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-[#D1CBBF]">
            <Globe className="w-16 h-16 text-[#D1CBBF] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0A1E40] mb-2">No countries found</h3>
            <Button onClick={() => setShowForm(true)} className="bg-[#FF5A36] hover:bg-[#E0492B]">
              Add Your First Country
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCountries.map((country) => (
              <div 
                key={country.country_id} 
                className="bg-white rounded-xl overflow-hidden border border-[#D1CBBF] card-hover"
                data-testid={`country-card-${country.code}`}
              >
                <div className="h-32 bg-cover bg-center relative" style={{ backgroundImage: `url(${country.image_url})` }}>
                  {country.is_popular && (
                    <span className="absolute top-2 right-2 bg-[#FF5A36] text-white text-xs px-2 py-1 rounded-full">
                      Popular
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CountryFlag code={country.code} size="lg" />
                    <h3 className="font-semibold text-[#0A1E40]">{country.name}</h3>
                    <span className="text-xs text-[#4A5568] bg-[#F5F0E6] px-2 py-1 rounded">{country.code}</span>
                  </div>
                  <p className="text-sm text-[#4A5568] line-clamp-2 mb-4">{country.description}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(country)}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(country.country_id)}>
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}                                                                  
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCountries;